// ✅ src/components/screens/CreateStoreScreen.js
// FULL WORKING “WIZARD” (Details → Payments → Delivery → Done)
// ✅ Removed "paste image url" inputs (banner/logo) — upload only
// ✅ Upload buttons are now PLUS tiles (click + to pick image)
// ✅ Bio is now SHORT (max chars) + counter + textarea height smaller
// ✅ Delivery fee input: no spinner arrows, no forced 0, empty allowed, clean number
// ✅ Prevent "040" issue by storing/typing as STRING then converting to Number on save
// ✅ FIX: Logo tile ALWAYS shows + overlay even when preview exists (so users know to click)

import React, { useEffect, useMemo, useState } from "react";
import { auth, db, storage } from "../../lib/firebase";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function safe(v) {
  return String(v ?? "").trim();
}

function makeSlug(name) {
  return safe(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function initials(name) {
  const t = safe(name);
  return t ? t.slice(0, 1).toUpperCase() : "U";
}

function pickExt(file) {
  const raw = safe(file?.name).split(".").pop() || "jpg";
  const ext = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
  return ext || "jpg";
}

// ✅ removes empty strings so Firestore doesn't store ""
function cleanPayload(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];

    if (typeof v === "boolean" || typeof v === "number") {
      out[k] = v;
      return;
    }
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = v;
      return;
    }

    const s = safe(v);
    if (s) out[k] = s;
  });
  return out;
}

async function uploadToFirebaseStorage(file, uid, kind) {
  if (!file || !uid) return "";

  const ext = pickExt(file);
  const path = `stores/${uid}/${kind}.${ext}`; // overwrites old
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file, {
    contentType: file.type || "image/jpeg",
  });

  const url = await getDownloadURL(storageRef);
  return safe(url);
}

async function ensureDefaultDocs(uid) {
  // payment methods (fixed IDs)
  await setDoc(
    doc(db, "stores", uid, "paymentMethods", "momo"),
    {
      type: "MOMO",
      label: "MTN MoMo",
      details: { network: "MTN", number: "", name: "" },
      isEnabled: true,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await setDoc(
    doc(db, "stores", uid, "paymentMethods", "bank"),
    {
      type: "BANK",
      label: "Bank Transfer",
      details: { bankName: "", accountName: "", accountNumber: "" },
      isEnabled: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await setDoc(
    doc(db, "stores", uid, "paymentMethods", "cash"),
    {
      type: "CASH",
      label: "Cash",
      details: { note: "Pay on pickup" },
      isEnabled: true,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  // delivery options (fixed IDs)
  await setDoc(
    doc(db, "stores", uid, "deliveryOptions", "delivery"),
    {
      type: "DELIVERY",
      title: "Delivery",
      fee: 40, // ✅ better default than 0
      etaText: "24 hours",
      areas: [],
      isEnabled: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await setDoc(
    doc(db, "stores", uid, "deliveryOptions", "pickup"),
    {
      type: "PICKUP",
      title: "Local Pickup",
      fee: 0,
      etaText: "",
      areas: [],
      isEnabled: true,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await setDoc(
    doc(db, "stores", uid, "deliveryOptions", "meetup"),
    {
      type: "MEETUP",
      title: "Meet-up Location",
      fee: 0,
      etaText: "",
      areas: [],
      isEnabled: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// ✅ Delivery fee input helpers
function digitsOnly(s) {
  return String(s || "").replace(/[^\d]/g, "");
}
function normalizeMoneyString(s, maxLen = 6) {
  const d = digitsOnly(s);
  if (!d) return "";
  // remove leading zeros: "040" -> "40"
  const noLead = d.replace(/^0+/, "");
  const out = noLead || "";
  return out.slice(0, maxLen);
}

export default function CreateStoreScreen({ onNext, goBack }) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 820 : false
  );

  // wizard step
  const [step, setStep] = useState("details"); // details | payments | delivery | done

  // auth profile
  const [displayName, setDisplayName] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [authEmail, setAuthEmail] = useState("");

  // store profile
  const [storeName, setStoreName] = useState("");
  const [bio, setBio] = useState("");
  const [locationText, setLocationText] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactWhatsApp, setContactWhatsApp] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  // images
  const [logoUrl, setLogoUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  // payments
  const [payMoMoEnabled, setPayMoMoEnabled] = useState(true);
  const [payMoMoNetwork, setPayMoMoNetwork] = useState("MTN");
  const [payMoMoNumber, setPayMoMoNumber] = useState("");
  const [payMoMoName, setPayMoMoName] = useState("");

  const [payBankEnabled, setPayBankEnabled] = useState(false);
  const [payBankName, setPayBankName] = useState("");
  const [payAccountName, setPayAccountName] = useState("");
  const [payAccountNumber, setPayAccountNumber] = useState("");

  const [payCashEnabled, setPayCashEnabled] = useState(true);
  const [payCashNote, setPayCashNote] = useState("Pay on pickup");

  // delivery
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryFeeStr, setDeliveryFeeStr] = useState(""); // ✅ string, not number
  const [deliveryEta, setDeliveryEta] = useState("24 hours");
  const [deliveryAreas, setDeliveryAreas] = useState("");

  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [pickupNote, setPickupNote] = useState("Contact me for pickup location");

  const [meetupEnabled, setMeetupEnabled] = useState(false);
  const [meetupNote, setMeetupNote] = useState("We can meet at a safe public place");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // ✅ BIO limits
  const BIO_MAX = 80;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // boot: load store + set defaults
  useEffect(() => {
    let alive = true;

    async function boot() {
      setMsg("");
      setLoading(true);

      const u = auth.currentUser;
      if (!u) {
        if (!alive) return;
        setLoading(false);
        setMsg("Please login first.");
        return;
      }

      const dn = safe(u.displayName);
      const em = safe(u.email);
      const ph = safe(u.photoURL);

      if (alive) {
        setDisplayName(dn);
        setAuthEmail(em);
        setPhotoURL(ph);
        setContactEmail((prev) => (safe(prev) ? prev : em));
      }

      try {
        await ensureDefaultDocs(u.uid);

        // load store doc
        const storeRef = doc(db, "stores", u.uid);
        const storeSnap = await getDoc(storeRef);
        const data = storeSnap.exists() ? storeSnap.data() : {};

        const bestName = safe(data?.storeName) || dn || "My Store";
        if (!alive) return;

        setStoreName(bestName);
        setBio(safe(data?.bio).slice(0, BIO_MAX));
        setLocationText(safe(data?.locationText));
        setContactPhone(safe(data?.contactPhone));
        setContactWhatsApp(safe(data?.contactWhatsApp));
        setContactEmail(safe(data?.contactEmail) || em);

        // image fallbacks
        setLogoUrl(safe(data?.logoUrl) || ph);
        setBannerUrl(safe(data?.bannerUrl));

        // load payment docs
        const momoSnap = await getDoc(doc(db, "stores", u.uid, "paymentMethods", "momo"));
        const bankSnap = await getDoc(doc(db, "stores", u.uid, "paymentMethods", "bank"));
        const cashSnap = await getDoc(doc(db, "stores", u.uid, "paymentMethods", "cash"));

        const momo = momoSnap.exists() ? momoSnap.data() : {};
        const bank = bankSnap.exists() ? bankSnap.data() : {};
        const cash = cashSnap.exists() ? cashSnap.data() : {};

        setPayMoMoEnabled(!!momo?.isEnabled);
        setPayMoMoNetwork(safe(momo?.details?.network) || "MTN");
        setPayMoMoNumber(safe(momo?.details?.number));
        setPayMoMoName(safe(momo?.details?.name));

        setPayBankEnabled(!!bank?.isEnabled);
        setPayBankName(safe(bank?.details?.bankName));
        setPayAccountName(safe(bank?.details?.accountName));
        setPayAccountNumber(safe(bank?.details?.accountNumber));

        setPayCashEnabled(!!cash?.isEnabled);
        setPayCashNote(safe(cash?.details?.note) || "Pay on pickup");

        // load delivery docs
        const delSnap = await getDoc(doc(db, "stores", u.uid, "deliveryOptions", "delivery"));
        const pickSnap = await getDoc(doc(db, "stores", u.uid, "deliveryOptions", "pickup"));
        const meetSnap = await getDoc(doc(db, "stores", u.uid, "deliveryOptions", "meetup"));

        const del = delSnap.exists() ? delSnap.data() : {};
        const pick = pickSnap.exists() ? pickSnap.data() : {};
        const meet = meetSnap.exists() ? meetSnap.data() : {};

        setDeliveryEnabled(!!del?.isEnabled);
        setDeliveryFeeStr(normalizeMoneyString(String(del?.fee ?? "")) || "40");
        setDeliveryEta(safe(del?.etaText) || "24 hours");
        setDeliveryAreas(Array.isArray(del?.areas) ? del.areas.join(", ") : "");

        setPickupEnabled(!!pick?.isEnabled);
        setPickupNote(safe(pick?.etaText) || "Contact me for pickup location");

        setMeetupEnabled(!!meet?.isEnabled);
        setMeetupNote(safe(meet?.etaText) || "We can meet at a safe public place");
      } catch (e) {
        if (alive) setMsg(e?.message || "Failed to load store setup.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    boot();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storeSlug = useMemo(() => makeSlug(storeName), [storeName]);

  const previewLogo = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    return safe(logoUrl);
  }, [logoFile, logoUrl]);

  const previewBanner = useMemo(() => {
    if (bannerFile) return URL.createObjectURL(bannerFile);
    return safe(bannerUrl);
  }, [bannerFile, bannerUrl]);

  useEffect(() => {
    return () => {
      try {
        if (logoFile && previewLogo) URL.revokeObjectURL(previewLogo);
        if (bannerFile && previewBanner) URL.revokeObjectURL(previewBanner);
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canDetails = safe(storeName).length >= 2 && safe(contactPhone).length >= 7;

  const S = useMemo(() => {
    return {
      page: {
        minHeight: "100vh",
        background: "#f4f7fb",
        padding: isMobile ? 14 : 24,
        display: "flex",
        justifyContent: "center",
        color: "#0f172a",
      },
      wrap: { width: "100%", maxWidth: 1050 },
      card: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 20,
        padding: isMobile ? 16 : 22,
        boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
      },
      top: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        marginBottom: 14,
        flexWrap: "wrap",
      },
      brand: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontWeight: 1000,
      },
      dot: {
        width: 10,
        height: 10,
        borderRadius: 999,
        background: "#0f172a",
      },
      title: { fontSize: isMobile ? 26 : 34, fontWeight: 1100, marginTop: 8 },
      sub: { color: "#475569", marginTop: 6, lineHeight: 1.6, fontSize: 14 },

      stepRow: {
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
        gap: 10,
        marginTop: 14,
        marginBottom: 18,
      },
      step: (active, done) => ({
        borderRadius: 14,
        border: active ? "2px solid #16a34a" : "1px solid #e5e7eb",
        background: done ? "rgba(22,163,74,0.06)" : "#fff",
        padding: 12,
        display: "grid",
        gap: 6,
      }),
      stepTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
      },
      stepNum: {
        width: 28,
        height: 28,
        borderRadius: 10,
        display: "grid",
        placeItems: "center",
        background: "#0f172a",
        color: "#fff",
        fontWeight: 1000,
        fontSize: 12,
      },
      stepName: { fontWeight: 1000, fontSize: 13 },
      stepHint: { fontSize: 12, color: "#64748b", fontWeight: 800 },

      grid2: {
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: 12,
        marginTop: 12,
      },
      field: { display: "grid", gap: 6 },
      label: { fontSize: 13, fontWeight: 1000 },
      input: {
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid #cbd5e1",
        outline: "none",
        fontSize: 14,
      },
      textarea: {
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid #cbd5e1",
        outline: "none",
        fontSize: 14,
        minHeight: 70,
        resize: "vertical",
      },
      helper: { fontSize: 12, color: "#64748b" },

      profileMini: {
        display: "flex",
        alignItems: "center",
        gap: 12,
      },
      avatar: {
        width: 46,
        height: 46,
        borderRadius: 999,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        background: "#0f172a",
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontWeight: 1000,
      },
      avatarImg: { width: "100%", height: "100%", objectFit: "cover" },

      bannerBox: {
        marginTop: 10,
        borderRadius: 16,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        background: "#f8fafc",
        height: isMobile ? 200 : 260,
        display: "grid",
        placeItems: "center",
        position: "relative",
      },
      bannerImg: { width: "100%", height: "100%", objectFit: "cover" },

      // ✅ plus uploader
      plusTile: (small) => ({
        width: small ? 78 : 92,
        height: small ? 78 : 92,
        borderRadius: 18,
        border: "1px dashed #cbd5e1",
        background: "#fff",
        display: "grid",
        placeItems: "center",
        cursor: saving ? "not-allowed" : "pointer",
        fontWeight: 1100,
        color: "#0f172a",
        userSelect: "none",
      }),
      plus: {
        width: 34,
        height: 34,
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        display: "grid",
        placeItems: "center",
        fontSize: 22,
        fontWeight: 1100,
        background: "#0f172a",
        color: "#fff",
      },
      plusSub: {
        marginTop: 8,
        fontSize: 12,
        color: "#64748b",
        fontWeight: 800,
        textAlign: "center",
        lineHeight: 1.25,
      },

      row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 16,
      },
      btn: (type) => ({
        padding: "12px 14px",
        borderRadius: 12,
        border: type === "primary" ? "none" : "1px solid #cbd5e1",
        background: type === "primary" ? "#0f172a" : "#fff",
        color: type === "primary" ? "#fff" : "#0f172a",
        fontWeight: 1000,
        cursor: "pointer",
        opacity: saving ? 0.7 : 1,
        minWidth: isMobile ? "100%" : 170,
      }),

      sectionTitle: { fontWeight: 1100, marginTop: 6, fontSize: 16 },
      box: {
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 14,
        background: "#fff",
      },
      toggleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
        marginBottom: 10,
      },

      error: {
        marginTop: 12,
        padding: 12,
        borderRadius: 12,
        border: "1px solid rgba(255, 60, 120, 0.35)",
        background: "rgba(255, 60, 120, 0.08)",
        color: "#b91c1c",
        fontSize: 13,
        fontWeight: 900,
      },

      note: {
        marginTop: 10,
        padding: 12,
        borderRadius: 12,
        border: "1px dashed #cbd5e1",
        background: "#f8fafc",
        fontSize: 12,
        color: "#475569",
        fontWeight: 800,
        lineHeight: 1.5,
      },

      // ✅ delivery fee input remove spinners
      noSpin: {
        MozAppearance: "textfield",
      },
    };
  }, [isMobile, saving]);

  const saveDetailsAndNext = async () => {
    setMsg("");

    const u = auth.currentUser;
    if (!u) return setMsg("Please login first.");
    if (!canDetails) return setMsg("Store name + phone are required.");

    try {
      setSaving(true);

      // upload to Firebase Storage if file selected
      let nextLogoUrl = safe(logoUrl) || safe(u.photoURL);
      let nextBannerUrl = safe(bannerUrl);

      if (logoFile) {
        const up = await uploadToFirebaseStorage(logoFile, u.uid, "logo");
        if (up) nextLogoUrl = up;
      }

      if (bannerFile) {
        const up = await uploadToFirebaseStorage(bannerFile, u.uid, "banner");
        if (up) nextBannerUrl = up;
      }

      const payloadRaw = {
        ownerId: u.uid,
        storeName: safe(storeName) || safe(u.displayName) || "My Store",
        storeSlug: makeSlug(safe(storeName) || safe(u.displayName) || "my-store"),
        bio: safe(bio).slice(0, BIO_MAX),
        locationText,
        contactPhone,
        contactWhatsApp,
        contactEmail: safe(contactEmail) || safe(u.email),
        logoUrl: nextLogoUrl,
        bannerUrl: nextBannerUrl,
        isActive: true,
        updatedAt: serverTimestamp(),
      };

      const storeRef = doc(db, "stores", u.uid);
      const beforeSnap = await getDoc(storeRef);
      const isNew = !beforeSnap.exists();

      const payload = cleanPayload(payloadRaw);
      if (isNew) payload.createdAt = serverTimestamp();

      await setDoc(storeRef, payload, { merge: true });
      await ensureDefaultDocs(u.uid);

      const afterSnap = await getDoc(storeRef);
      const afterData = afterSnap.exists() ? afterSnap.data() : {};

      setStoreName(safe(afterData?.storeName) || payloadRaw.storeName);
      setLogoUrl(safe(afterData?.logoUrl) || nextLogoUrl);
      setBannerUrl(safe(afterData?.bannerUrl) || nextBannerUrl);

      setStep("payments");
    } catch (e) {
      setMsg(e?.message || "Failed to save store details.");
    } finally {
      setSaving(false);
    }
  };

  const savePaymentsAndNext = async () => {
    setMsg("");

    const u = auth.currentUser;
    if (!u) return setMsg("Please login first.");

    try {
      setSaving(true);

      await setDoc(
        doc(db, "stores", u.uid, "paymentMethods", "momo"),
        {
          type: "MOMO",
          label: "Mobile Money",
          details: {
            network: safe(payMoMoNetwork),
            number: safe(payMoMoNumber),
            name: safe(payMoMoName),
          },
          isEnabled: !!payMoMoEnabled,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "stores", u.uid, "paymentMethods", "bank"),
        {
          type: "BANK",
          label: "Bank Transfer",
          details: {
            bankName: safe(payBankName),
            accountName: safe(payAccountName),
            accountNumber: safe(payAccountNumber),
          },
          isEnabled: !!payBankEnabled,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "stores", u.uid, "paymentMethods", "cash"),
        {
          type: "CASH",
          label: "Cash",
          details: { note: safe(payCashNote) },
          isEnabled: !!payCashEnabled,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setStep("delivery");
    } catch (e) {
      setMsg(e?.message || "Failed to save payment methods.");
    } finally {
      setSaving(false);
    }
  };

  const saveDeliveryAndFinish = async () => {
    setMsg("");

    const u = auth.currentUser;
    if (!u) return setMsg("Please login first.");

    try {
      setSaving(true);

      const areasArray = safe(deliveryAreas)
        ? safe(deliveryAreas)
            .split(",")
            .map((x) => safe(x))
            .filter(Boolean)
        : [];

      const feeClean = normalizeMoneyString(deliveryFeeStr);
      const feeNum = feeClean ? Number(feeClean) : null;

      if (deliveryEnabled) {
        if (!feeNum || feeNum <= 0) {
          setSaving(false);
          setMsg("Delivery fee is required (must be greater than 0).");
          return;
        }
      }

      await setDoc(
        doc(db, "stores", u.uid, "deliveryOptions", "delivery"),
        {
          type: "DELIVERY",
          title: "Delivery",
          fee: feeNum || 0,
          etaText: safe(deliveryEta),
          areas: areasArray,
          isEnabled: !!deliveryEnabled,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "stores", u.uid, "deliveryOptions", "pickup"),
        {
          type: "PICKUP",
          title: "Local Pickup",
          fee: 0,
          etaText: safe(pickupNote),
          areas: [],
          isEnabled: !!pickupEnabled,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "stores", u.uid, "deliveryOptions", "meetup"),
        {
          type: "MEETUP",
          title: "Meet-up Location",
          fee: 0,
          etaText: safe(meetupNote),
          areas: [],
          isEnabled: !!meetupEnabled,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await updateDoc(doc(db, "stores", u.uid), {
        setupDone: true,
        setupStep: "done",
        updatedAt: serverTimestamp(),
      });

      setStep("done");

      onNext?.({
        storeId: u.uid,
        storeSlug,
      });
    } catch (e) {
      setMsg(e?.message || "Failed to save delivery options.");
    } finally {
      setSaving(false);
    }
  };

  const StepCards = () => {
    const doneDetails = step !== "details";
    const donePayments = step === "delivery" || step === "done";
    const doneDelivery = step === "done";
    return (
      <div style={S.stepRow}>
        <div style={S.step(step === "details", doneDetails)}>
          <div style={S.stepTop}>
            <div style={S.stepNum}>1</div>
            <div style={{ textAlign: "right" }}>
              <div style={S.stepName}>Store Details</div>
              <div style={S.stepHint}>Profile + banner</div>
            </div>
          </div>
        </div>

        <div style={S.step(step === "payments", donePayments)}>
          <div style={S.stepTop}>
            <div style={S.stepNum}>2</div>
            <div style={{ textAlign: "right" }}>
              <div style={S.stepName}>Payments</div>
              <div style={S.stepHint}>MoMo / Bank / Cash</div>
            </div>
          </div>
        </div>

        <div style={S.step(step === "delivery", doneDelivery)}>
          <div style={S.stepTop}>
            <div style={S.stepNum}>3</div>
            <div style={{ textAlign: "right" }}>
              <div style={S.stepName}>Delivery</div>
              <div style={S.stepHint}>Delivery / Pickup</div>
            </div>
          </div>
        </div>

        <div style={S.step(step === "done", step === "done")}>
          <div style={S.stepTop}>
            <div style={S.stepNum}>4</div>
            <div style={{ textAlign: "right" }}>
              <div style={S.stepName}>Dashboard</div>
              <div style={S.stepHint}>Manage store</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Header = () => (
    <div style={S.top}>
      <div style={S.brand}>
        <span style={S.dot} />
        <span>Lirivelle</span>
      </div>

      <div style={S.profileMini}>
        <div style={S.avatar}>
          {photoURL ? (
            <img src={photoURL} alt="profile" style={S.avatarImg} />
          ) : (
            initials(displayName || storeName)
          )}
        </div>
        <div style={{ display: "grid", gap: 2 }}>
          <div style={{ fontWeight: 1000 }}>{displayName || "Your profile"}</div>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>
            {authEmail || "—"}
          </div>
        </div>
      </div>
    </div>
  );

  // ✅ PLUS upload components (FIXED: show + overlay even when preview exists)
  const PlusUploader = ({ label, small, previewSrc, onPick }) => {
    return (
      <div style={{ display: "grid", gap: 8 }}>
        <div style={S.label}>{label}</div>

        <div
          style={{
            ...S.plusTile(small),
            padding: 0,
            border: previewSrc ? "1px solid #e5e7eb" : "1px dashed #cbd5e1",
            overflow: "hidden",
            background: "#f8fafc",
            position: "relative",
            cursor: saving ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            if (saving) return;
            onPick?.();
          }}
          title={previewSrc ? "Click to change" : "Add image"}
        >
          {previewSrc ? (
            <>
              <img
                src={previewSrc}
                alt={label}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />

              {/* ✅ visible PLUS badge */}
              <div
                style={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  width: 30,
                  height: 30,
                  borderRadius: 12,
                  background: "rgba(15,23,42,0.88)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 20,
                  fontWeight: 1100,
                  border: "1px solid rgba(255,255,255,0.25)",
                }}
              >
                +
              </div>

              {/* ✅ “Change” hint */}
              <div
                style={{
                  position: "absolute",
                  left: 8,
                  bottom: 8,
                  padding: "6px 8px",
                  borderRadius: 10,
                  background: "rgba(15,23,42,0.70)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 900,
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              >
                Change
              </div>
            </>
          ) : (
            <div style={{ display: "grid", placeItems: "center" }}>
              <div style={S.plus}>+</div>
              <div style={S.plusSub}>Add image</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.card}>
          <Header />
          <StepCards />

          <div style={S.title}>
            {step === "details"
              ? "Store details"
              : step === "payments"
              ? "Payment methods"
              : step === "delivery"
              ? "Delivery options"
              : "All done"}
          </div>

          <div style={S.sub}>
            {step === "details"
              ? "Add banner, logo, bio and contact. Then continue to payments."
              : step === "payments"
              ? "Choose how buyers can pay you (MoMo / Bank / Cash)."
              : step === "delivery"
              ? "Choose delivery / pickup options for buyers."
              : "Setup complete. Redirecting to your Seller Dashboard."}
          </div>

          {loading ? <div style={S.note}>Loading your store setup…</div> : null}

          {step === "details" && !loading ? (
            <>
              {/* hidden inputs for PLUS upload */}
              <input
                id="bannerPicker"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={saving}
                onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
              />
              <input
                id="logoPicker"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={saving}
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              />

              {/* Banner + Logo row */}
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={S.label}>Store banner</div>
                    <div style={S.bannerBox}>
                      {previewBanner ? (
                        <img src={previewBanner} alt="banner" style={S.bannerImg} />
                      ) : (
                        <div style={{ display: "grid", placeItems: "center", gap: 8 }}>
                          <div
                            style={S.plusTile(false)}
                            onClick={() => {
                              if (saving) return;
                              const el = document.getElementById("bannerPicker");
                              if (el) el.click();
                            }}
                          >
                            <div style={{ display: "grid", placeItems: "center" }}>
                              <div style={S.plus}>+</div>
                              <div style={S.plusSub}>Add banner</div>
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>
                            Recommended: wide image
                          </div>
                        </div>
                      )}

                      {/* Change overlay */}
                      {previewBanner ? (
                        <div
                          onClick={() => {
                            if (saving) return;
                            const el = document.getElementById("bannerPicker");
                            if (el) el.click();
                          }}
                          style={{
                            position: "absolute",
                            right: 10,
                            top: 10,
                            background: "rgba(15,23,42,0.85)",
                            color: "#fff",
                            padding: "8px 10px",
                            borderRadius: 12,
                            fontWeight: 900,
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          + Change
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ width: 160 }}>
                    <PlusUploader
                      label="Store logo"
                      small={true}
                      previewSrc={previewLogo}
                      onPick={() => {
                        const el = document.getElementById("logoPicker");
                        if (el) el.click();
                      }}
                    />
                  </div>
                </div>

                <div style={S.note}>
                  ✅ Images upload to <b>Firebase Storage</b> when you click Continue.
                </div>
              </div>

              <div style={S.grid2}>
                <div style={S.field}>
                  <div style={S.label}>Store name *</div>
                  <input
                    style={S.input}
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    disabled={saving}
                  />
                  <div style={S.helper}>This is what buyers see.</div>
                </div>

                <div style={S.field}>
                  <div style={S.label}>Store location</div>
                  <input
                    style={S.input}
                    value={locationText}
                    onChange={(e) => setLocationText(e.target.value)}
                    disabled={saving}
                  />
                </div>

                <div style={S.field}>
                  <div style={S.label}>Phone *</div>
                  <input
                    style={S.input}
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    disabled={saving}
                  />
                </div>

                <div style={S.field}>
                  <div style={S.label}>WhatsApp</div>
                  <input
                    style={S.input}
                    value={contactWhatsApp}
                    onChange={(e) => setContactWhatsApp(e.target.value)}
                    disabled={saving}
                  />
                </div>

                <div style={S.field}>
                  <div style={S.label}>Contact email</div>
                  <input
                    style={S.input}
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    disabled={saving}
                  />
                </div>

                <div style={S.field}>
                  <div style={S.label}>
                    Bio / What you sell{" "}
                    <span style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                      ({safe(bio).length}/{BIO_MAX})
                    </span>
                  </div>
                  <textarea
                    style={S.textarea}
                    value={bio}
                    onChange={(e) =>
                      setBio(String(e.target.value || "").slice(0, BIO_MAX))
                    }
                    disabled={saving}
                    placeholder="Short description (max 80 characters)"
                  />
                  <div style={S.helper}>Keep it short.</div>
                </div>
              </div>

              <div style={S.note}>
                Your store link will be: <b>/s/{storeSlug || "your-store"}</b>
              </div>

              <div style={S.row}>
                <button style={S.btn("outline")} onClick={goBack} disabled={saving}>
                  ← Back
                </button>

                <button
                  style={S.btn("primary")}
                  onClick={saveDetailsAndNext}
                  disabled={saving || !canDetails}
                >
                  {saving ? "Saving..." : "Continue → Payments"}
                </button>
              </div>
            </>
          ) : null}

          {step === "payments" && !loading ? (
            <>
              <div style={S.grid2}>
                {/* MOMO */}
                <div style={S.box}>
                  <div style={S.toggleRow}>
                    <div style={S.sectionTitle}>📱 Mobile Money (MoMo)</div>
                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontWeight: 900, color: "#475569" }}>Enabled</span>
                      <input
                        type="checkbox"
                        checked={payMoMoEnabled}
                        onChange={(e) => setPayMoMoEnabled(e.target.checked)}
                      />
                    </label>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Network</div>
                    <select
                      style={S.input}
                      value={payMoMoNetwork}
                      onChange={(e) => setPayMoMoNetwork(e.target.value)}
                      disabled={!payMoMoEnabled}
                    >
                      <option value="MTN">MTN</option>
                      <option value="TELECEL">Telecel</option>
                      <option value="AIRTELTIGO">AirtelTigo</option>
                    </select>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>MoMo number</div>
                    <input
                      style={S.input}
                      placeholder="024xxxxxxx"
                      value={payMoMoNumber}
                      onChange={(e) => setPayMoMoNumber(e.target.value)}
                      disabled={!payMoMoEnabled}
                    />
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>MoMo name</div>
                    <input
                      style={S.input}
                      placeholder="Account name (e.g. Ama Mensah)"
                      value={payMoMoName}
                      onChange={(e) => setPayMoMoName(e.target.value)}
                      disabled={!payMoMoEnabled}
                    />
                  </div>
                </div>

                {/* BANK */}
                <div style={S.box}>
                  <div style={S.toggleRow}>
                    <div style={S.sectionTitle}>🏦 Bank Transfer</div>
                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontWeight: 900, color: "#475569" }}>Enabled</span>
                      <input
                        type="checkbox"
                        checked={payBankEnabled}
                        onChange={(e) => setPayBankEnabled(e.target.checked)}
                      />
                    </label>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Bank name</div>
                    <input
                      style={S.input}
                      value={payBankName}
                      onChange={(e) => setPayBankName(e.target.value)}
                      disabled={!payBankEnabled}
                    />
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Account name</div>
                    <input
                      style={S.input}
                      value={payAccountName}
                      onChange={(e) => setPayAccountName(e.target.value)}
                      disabled={!payBankEnabled}
                    />
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Account number</div>
                    <input
                      style={S.input}
                      value={payAccountNumber}
                      onChange={(e) => setPayAccountNumber(e.target.value)}
                      disabled={!payBankEnabled}
                    />
                  </div>
                </div>

                {/* CASH */}
                <div style={S.box}>
                  <div style={S.toggleRow}>
                    <div style={S.sectionTitle}>💵 Cash</div>
                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontWeight: 900, color: "#475569" }}>Enabled</span>
                      <input
                        type="checkbox"
                        checked={payCashEnabled}
                        onChange={(e) => setPayCashEnabled(e.target.checked)}
                      />
                    </label>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Cash note</div>
                    <input
                      style={S.input}
                      value={payCashNote}
                      onChange={(e) => setPayCashNote(e.target.value)}
                      disabled={!payCashEnabled}
                    />
                  </div>

                  <div style={S.helper}>Example: “Pay on pickup” or “Cash at meetup”</div>
                </div>
              </div>

              <div style={S.row}>
                <button
                  style={S.btn("outline")}
                  onClick={() => setStep("details")}
                  disabled={saving}
                >
                  ← Back
                </button>

                <button style={S.btn("primary")} onClick={savePaymentsAndNext} disabled={saving}>
                  {saving ? "Saving..." : "Continue → Delivery"}
                </button>
              </div>
            </>
          ) : null}

          {step === "delivery" && !loading ? (
            <>
              <div style={S.grid2}>
                {/* DELIVERY */}
                <div style={S.box}>
                  <div style={S.toggleRow}>
                    <div style={S.sectionTitle}>🚚 Delivery</div>
                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontWeight: 900, color: "#475569" }}>Enabled</span>
                      <input
                        type="checkbox"
                        checked={deliveryEnabled}
                        onChange={(e) => setDeliveryEnabled(e.target.checked)}
                      />
                    </label>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Delivery fee (GHS)</div>
                    <input
                      style={{ ...S.input, ...S.noSpin }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 40"
                      value={deliveryFeeStr}
                      onChange={(e) => setDeliveryFeeStr(normalizeMoneyString(e.target.value))}
                      disabled={!deliveryEnabled}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                    <div style={S.helper}>Numbers only. No leading zero.</div>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>ETA text</div>
                    <input
                      style={S.input}
                      value={deliveryEta}
                      onChange={(e) => setDeliveryEta(e.target.value)}
                      disabled={!deliveryEnabled}
                    />
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Areas (comma separated)</div>
                    <input
                      style={S.input}
                      placeholder="Accra, Tema, Madina"
                      value={deliveryAreas}
                      onChange={(e) => setDeliveryAreas(e.target.value)}
                      disabled={!deliveryEnabled}
                    />
                  </div>
                </div>

                {/* PICKUP */}
                <div style={S.box}>
                  <div style={S.toggleRow}>
                    <div style={S.sectionTitle}>📍 Local Pickup</div>
                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontWeight: 900, color: "#475569" }}>Enabled</span>
                      <input
                        type="checkbox"
                        checked={pickupEnabled}
                        onChange={(e) => setPickupEnabled(e.target.checked)}
                      />
                    </label>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Pickup note</div>
                    <input
                      style={S.input}
                      value={pickupNote}
                      onChange={(e) => setPickupNote(e.target.value)}
                      disabled={!pickupEnabled}
                    />
                  </div>
                </div>

                {/* MEETUP */}
                <div style={S.box}>
                  <div style={S.toggleRow}>
                    <div style={S.sectionTitle}>🤝 Meet-up Location</div>
                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontWeight: 900, color: "#475569" }}>Enabled</span>
                      <input
                        type="checkbox"
                        checked={meetupEnabled}
                        onChange={(e) => setMeetupEnabled(e.target.checked)}
                      />
                    </label>
                  </div>

                  <div style={S.field}>
                    <div style={S.label}>Meet-up note</div>
                    <input
                      style={S.input}
                      value={meetupNote}
                      onChange={(e) => setMeetupNote(e.target.value)}
                      disabled={!meetupEnabled}
                    />
                  </div>
                </div>
              </div>

              <div style={S.row}>
                <button
                  style={S.btn("outline")}
                  onClick={() => setStep("payments")}
                  disabled={saving}
                >
                  ← Back
                </button>

                <button style={S.btn("primary")} onClick={saveDeliveryAndFinish} disabled={saving}>
                  {saving ? "Saving..." : "Finish → Seller Dashboard"}
                </button>
              </div>
            </>
          ) : null}

          {msg ? <div style={S.error}>{msg}</div> : null}
        </div>
      </div>

      {/* ✅ inline CSS to remove number spinners in webkit (kept for safety) */}
      <style>{`
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `}</style>
    </div>
  );
}
